/*
 * ImportProgress — the logged-in user's test imports, in the header left of
 * the search box: a thin bar per running import, "Ready" when it lands, and
 * a failure note. Polls the quiz API; renders nothing when there is nothing
 * to show.
 */

import { FC, useEffect, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { loggedUserInfoStore } from '@/stores';
import { getUploadStatus, type UploadJob } from '@/services/client/quiz';

import './index.scss';

/** Fired by the upload form so the bar appears at once, not at the next poll. */
export const UPLOAD_QUEUED_EVENT = 'aiforu2:upload-queued';

// Pipeline stages with a label (practice_tests.stage_<name>).
const STAGES = [
  'starting',
  'pictures',
  'extract',
  'review',
  'judge',
  'figures',
  'import',
];

const ImportProgress: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'practice_tests' });
  const { user } = loggedUserInfoStore((_) => _);
  const [jobs, setJobs] = useState<UploadJob[]>([]);

  useEffect(() => {
    if (!user.access_token) {
      setJobs([]);
      return undefined;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      let next: UploadJob[] = [];
      try {
        next = (await getUploadStatus()).jobs;
      } catch {
        // non-critical: keep the last known state
      }
      if (cancelled) return;
      setJobs(next);
      // Every 20s while something is running, every 30s otherwise.
      const active = next.some(
        (j) => j.state === 'queued' || j.state === 'processing',
      );
      timer = setTimeout(poll, active ? 20000 : 30000);
    };
    // Check again right away when an upload is queued here, or when the
    // tab comes back into view.
    const now = () => {
      clearTimeout(timer);
      poll();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') now();
    };
    poll();
    window.addEventListener(UPLOAD_QUEUED_EVENT, now);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener(UPLOAD_QUEUED_EVENT, now);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user.access_token]);

  const running = jobs.filter(
    (j) => j.state === 'queued' || j.state === 'processing',
  );
  const job = running[0] || jobs[jobs.length - 1];
  if (!job) return null;

  const more = running.length > 1 ? ` +${running.length - 1}` : '';
  if (job.state === 'done') {
    return (
      <Link to="/tests" className="import-progress import-progress--done">
        {t('import_ready')}{' '}
        <span className="import-progress__title">{job.title}</span>
      </Link>
    );
  }
  if (job.state === 'failed') {
    return (
      <Link
        to="/tests"
        className="import-progress import-progress--failed"
        title={job.message || ''}>
        {t('import_failed')}{' '}
        <span className="import-progress__title">{job.title}</span>
      </Link>
    );
  }
  const label =
    job.state === 'queued'
      ? t('import_waiting')
      : STAGES.includes(job.stage || '')
        ? t(`stage_${job.stage}`)
        : t('import_importing');
  return (
    <Link
      to="/tests"
      className="import-progress"
      title={`${job.title} — ${label}, ${job.percent}%`}>
      <div className="import-progress__row">
        <span className="import-progress__title">{job.title}</span>
        <span className="import-progress__pct">
          {job.percent}%{more}
        </span>
      </div>
      <ProgressBar
        now={Math.max(job.percent, 3)}
        animated={job.state === 'processing'}
        className="import-progress__bar"
      />
      <div className="import-progress__stage">{label}</div>
    </Link>
  );
};

export default ImportProgress;
